/**
 * "I only know so far ahead" — SPEC §3.3.
 *
 * The case this exists for: an instructor who genuinely does not know their back half. The
 * generic idea of a "partial" month handled it badly, because a half-filled month and a
 * deliberately-sparse month look identical in the data.
 *
 * ## The horizon belongs to the person (§3.3.1)
 *
 * Setting a date makes everything up to and including it confirmed, and everything after it
 * unconfirmed — the rest of that month **and every later month shown**. The first version
 * scoped it to one month, which allowed "unsure past September 15" to sit beside "all of
 * October confirmed", a state that cannot be true.
 *
 * Submitting a later month overrides the shadow for that month, so this control has three
 * distinct things to say depending on where the horizon sits relative to the month on screen.
 *
 * Deliberately collapsed by default in the ordinary case. Most instructors know their whole
 * month, and a control they will never use must not slow down the three-tap path (§12.2).
 */

import { compare, datesInCycle, toISO, type CalendarDate } from '@gearup/shared';
import { formatDayLong } from './MonthCalendar.js';

export interface ConfirmedThroughProps {
  cycleKey: string;
  monthName: string;
  /** The instructor's horizon, person-level. Null when none stands. */
  value: string | null;
  /** Whether that horizon shadows this month — false once overridden by submitting it. */
  applies: boolean;
  /** True when the horizon sits before this month entirely, shadowing all of it. */
  wholeMonthUnconfirmed: boolean;
  today: CalendarDate;
  disabled?: boolean;
  onChange: (value: string | null) => void;
}

export function ConfirmedThrough({
  cycleKey,
  monthName,
  value,
  applies,
  wholeMonthUnconfirmed,
  today,
  disabled,
  onChange,
}: ConfirmedThroughProps) {
  // Past dates cannot be a confirmation horizon — confirming through yesterday says nothing.
  const options = datesInCycle(cycleKey).filter((d) => compare(d, today) >= 0);
  const last = options[options.length - 1];

  // A horizon on the last day of the month means the whole month is confirmed, which is
  // just a normal complete submission — so it is not offered.
  const selectable = options.slice(0, -1);

  const picker = (label: string) => (
    <label className="field">
      <span className="field__label">{label}</span>
      <select
        className="select"
        defaultValue=""
        disabled={disabled}
        onChange={(e) => e.target.value && onChange(e.target.value)}
      >
        <option value="" disabled>
          Pick a date…
        </option>
        {selectable.map((d) => (
          <option key={toISO(d)} value={toISO(d)}>
            {formatDayLong(toISO(d))}
          </option>
        ))}
      </select>
    </label>
  );

  // -----------------------------------------------------------------------
  // The horizon sits before this month, and still shadows it (§3.3.1).
  //
  // The instructor is looking at October having told us they are only sure through
  // September 15. Everything marked here is provisional, and the way out is simply to
  // submit — which is the positive statement "I do know October".
  // -----------------------------------------------------------------------
  if (value && applies && wholeMonthUnconfirmed) {
    return (
      <div className="notice notice--warn" style={{ alignItems: 'flex-start' }}>
        <span className="notice__icon" aria-hidden="true">
          ~
        </span>
        <span className="grow">
          You told us you're only sure through <strong>{formatDayLong(value)}</strong>, so
          everything you've marked in {monthName} is shown as <em>not confirmed yet</em>.
          <br />
          <span className="small muted">
            If you do know {monthName}, just submit it — that tells us you're sure about this
            month, and the rest stays as it was.
          </span>
          {selectable.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', minHeight: 32 }}>
                I know part of {monthName}
              </summary>
              <div className="stack" style={{ marginTop: 8 }}>
                {picker(`I'm sure through`)}
                <p className="small muted" style={{ margin: 0 }}>
                  Picking a date here also confirms everything before it.
                </p>
              </div>
            </details>
          )}
        </span>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // The horizon lands inside this month — the original case.
  // -----------------------------------------------------------------------
  if (value && applies) {
    return (
      <div className="notice notice--warn" style={{ alignItems: 'flex-start' }}>
        <span className="notice__icon" aria-hidden="true">
          ~
        </span>
        <span className="grow">
          You're sure through <strong>{formatDayLong(value)}</strong>. Anything marked after
          that — in {monthName} and in the months after it — is shown as{' '}
          <em>not confirmed yet</em>, and we'll check back with you about it.
          <br />
          <button
            type="button"
            className="btn btn--ghost"
            style={{ padding: '4px 0', minHeight: 32 }}
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            Actually, I know the whole month
          </button>
        </span>
      </div>
    );
  }

  if (selectable.length === 0) return null;

  // -----------------------------------------------------------------------
  // No horizon, or one this month has already overridden by being submitted.
  // -----------------------------------------------------------------------
  return (
    <details>
      <summary
        style={{ cursor: 'pointer', minHeight: 40, display: 'flex', alignItems: 'center' }}
      >
        I don't know the rest of {monthName} yet
      </summary>

      <div className="stack" style={{ marginTop: 12 }}>
        <p className="small muted" style={{ margin: 0 }}>
          Tell us how far ahead you're sure. We'll take what you've given us and ask again
          about the rest, rather than chasing you for the whole month.
        </p>
        {picker(`I'm sure through`)}
        {value && !applies && (
          // The override case: they confirmed this month while an earlier horizon stands.
          // Saying so out loud stops "why is September still amber?" being a mystery.
          <p className="small muted" style={{ margin: 0 }}>
            You've confirmed all of {monthName}. You're still only sure through{' '}
            {formatDayLong(value)} elsewhere.
          </p>
        )}
        {last && (
          <p className="small muted" style={{ margin: 0 }}>
            {monthName} ends on {formatDayLong(toISO(last))}.
          </p>
        )}
      </div>
    </details>
  );
}
